import { getDocs, getDoc, query, where } from 'firebase/firestore';
import type { DocumentData } from 'firebase/firestore';
import { getCollectionRef, getDocumentRef, handleFirebaseError } from './firebaseUtils';
import { workspaceApi } from './workspaceApi';
import { getCurrentPromotionIds } from './promotionService';
import { WorkspaceScope } from '../types/workspace';
import { PromotionViewStats, PromotionStatsSummary, CrudResult } from '../types';
import { getAnonymousVisitorId } from '../utils/anonymousVisitor';
import { isLocalStatisticsHost, summarizeViewRecords } from '../config/visitorStatistics.mjs';

async function result<T>(operation: () => Promise<T>): Promise<CrudResult<T>> {
  try { return { success: true, data: await operation() }; }
  catch (error) { return { success: false, error: handleFirebaseError(error) }; }
}
export const recordPromotionView = (scope: WorkspaceScope, promotionId: string): Promise<CrudResult<void>> => result(async () => {
  // 관리자 미리보기는 공개 조회수에 포함하지 않는다.
  if (scope.mode !== 'public' || isLocalStatisticsHost(window.location.hostname)) return;
  await new Promise<void>(resolve => setTimeout(resolve, 0));
  const visitorId = await getAnonymousVisitorId();
  await workspaceApi('recordView', scope, { identifier: scope.publicIdentifier, promotionId, ...(visitorId ? { visitorId } : {}) });
});
export const getPromotionStats = (scope: WorkspaceScope, promotionId: string): Promise<CrudResult<PromotionViewStats | null>> => result(async () => {
  if (!(await getCurrentPromotionIds(scope)).has(promotionId)) return null;
  const snapshot = await getDoc(getDocumentRef(scope, 'promotionStats', `stats_${promotionId}`));
  if (!snapshot.exists()) return null;
  const data = snapshot.data();
  const events = await getDocs(query(getCollectionRef(scope, 'viewEvents'), where('promotionId', '==', promotionId)));
  const history = events.docs.filter(item => item.data().epoch === (data.epoch || 0)).map(item => item.data());
  return { ...data, id: snapshot.id, viewHistory: [...(data.epoch ? [] : data.viewHistory || []), ...history] } as PromotionViewStats;
});
export const getAllPromotionStats = (scope: WorkspaceScope): Promise<CrudResult<PromotionViewStats[]>> => result(async () => {
  const [snapshot, events, currentIds] = await Promise.all([getDocs(getCollectionRef(scope, 'promotionStats')), getDocs(getCollectionRef(scope, 'viewEvents')), getCurrentPromotionIds(scope)]);
  const eventsByPromotion = new Map<unknown, Map<unknown, DocumentData[]>>();
  for (const event of events.docs) {
    const data = event.data();
    let epochs = eventsByPromotion.get(data.promotionId);
    if (!epochs) { epochs = new Map(); eventsByPromotion.set(data.promotionId, epochs); }
    const history = epochs.get(data.epoch);
    if (history) history.push(data);
    else epochs.set(data.epoch, [data]);
  }
  return snapshot.docs.filter(item => currentIds.has(item.data().promotionId)).map(item => {
    const data = item.data();
    const history = eventsByPromotion.get(data.promotionId)?.get(data.epoch || 0) || [];
    return { ...data, id: item.id, viewHistory: [...(data.epoch ? [] : data.viewHistory || []), ...history] } as PromotionViewStats;
  });
});
export const getPromotionStatsSummary = (scope: WorkspaceScope, ids: string[]): Promise<CrudResult<Record<string, PromotionStatsSummary>>> => result(async () => {
  const summaries: Record<string, PromotionStatsSummary> = {};
  await Promise.all(ids.map(async id => {
    const snapshot = await getDoc(getDocumentRef(scope, 'promotionStats', `stats_${id}`));
    summaries[id] = { promotionId: id, totalViews: snapshot.data()?.totalViews || 0, uniqueIPCount: snapshot.data()?.uniqueIPCount || 0, uniqueVisitorsV2: snapshot.data()?.uniqueVisitorsV2 || 0 };
  }));
  return summaries;
});
function topStatsFromData(data: PromotionViewStats[], field: 'totalViews' | 'uniqueVisitorsV2', count: number): PromotionStatsSummary[] {
  return [...data].sort((a, b) => (b[field] || 0) - (a[field] || 0)).slice(0, count).map(({ promotionId, totalViews, uniqueIPCount, uniqueVisitorsV2 }) => ({ promotionId, totalViews, uniqueIPCount, uniqueVisitorsV2: uniqueVisitorsV2 || 0 }));
}
async function topStats(scope: WorkspaceScope, field: 'totalViews' | 'uniqueVisitorsV2', count: number): Promise<PromotionStatsSummary[]> {
  const response = await getAllPromotionStats(scope);
  if (!response.success || !response.data) throw new Error(response.error);
  return topStatsFromData(response.data, field, count);
}
export const getTopViewedPromotions = (scope: WorkspaceScope, count = 10): Promise<CrudResult<PromotionStatsSummary[]>> => result(() => topStats(scope, 'totalViews', count));
export const getTopUserPromotions = (scope: WorkspaceScope, count = 10): Promise<CrudResult<PromotionStatsSummary[]>> => result(() => topStats(scope, 'uniqueVisitorsV2', count));
export const resetPromotionStats = (scope: WorkspaceScope, id: string): Promise<CrudResult<void>> => result(async () => {
  await workspaceApi('resetStats', scope, { id });
});
export const deletePromotionStats = resetPromotionStats;
function dashboardStatsFromData(data: PromotionViewStats[]) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();
  const month = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const ranges = { yesterday: { start: yesterday, end: today }, today: { start: today, end: Infinity }, thisMonth: { start: month, end: Infinity } };
  const count = (start: number, end: number) => {
    const records = data.flatMap(item => item.viewHistory || []).filter(item => item.viewedAt.toMillis() >= start && item.viewedAt.toMillis() < end);
    return summarizeViewRecords(records);
  };
  return { yesterday: count(ranges.yesterday.start, ranges.yesterday.end), today: count(ranges.today.start, ranges.today.end), thisMonth: count(ranges.thisMonth.start, ranges.thisMonth.end) };
}
export const getDashboardStats = (scope: WorkspaceScope) => result(async () => {
  const response = await getAllPromotionStats(scope);
  if (!response.success || !response.data) throw new Error(response.error);
  return dashboardStatsFromData(response.data);
});
export const getDashboardOverview = (scope: WorkspaceScope) => result(async () => {
  const response = await getAllPromotionStats(scope);
  if (!response.success || !response.data) throw new Error(response.error);
  return {
    viewStats: dashboardStatsFromData(response.data),
    topViewedPromotion: topStatsFromData(response.data, 'totalViews', 1)[0] || null,
    topUserPromotion: topStatsFromData(response.data, 'uniqueVisitorsV2', 1)[0] || null
  };
});
